import { configure, makeAutoObservable } from 'mobx';
import { debugLog } from '../debug/devLogger';

configure({
  enforceActions: 'never',
});

// Cross-tab session channel. The selected org (fein) and drug (ndc), and the
// signed-out state, live ONLY in memory; BroadcastChannel mirrors those changes
// to other open tabs of the same origin so a selection or sign-out in one tab
// follows the user everywhere. Nothing is ever written to browser storage.
const SESSION_CHANNEL = 'uid-pharma-session';

export default new (class {
  // the current page title
  pageTitle = '';

  // how many outstanding ajax requests are running
  isAjaxing = 0;
  // the user from the webservice from jwt
  loggedInUser = null;
  // user from oidc service (Firebase ID token mirror)
  oidcUser = null;

  // pharmacy-user business key (email) of the signed-in user. All session and
  // role state lives ONLY here in memory — never persisted to browser storage.
  email = undefined;

  // selected drug — the drug BUSINESS KEY (ndc). Never a natural key; never
  // persisted.
  currentDrug = undefined;

  // selected org — the org BUSINESS KEY (fein). The full org record is resolved
  // on demand from userOrgs via `currentOrgRecord`. Never a natural key; never
  // persisted.
  currentOrg = undefined;

  userOrgs = [];

  // Role names the signed-in user holds (e.g. 'Administrator',
  // 'Org Administrator'), derived from the backend profile roles. In-memory
  // only; declared here so MobX makes it observable.
  userRoles = [];

  showMessage = false;

  // True when the signed-in user's only org-administered company has been put
  // back into review (e.g. after an FEIN change). While set, the app is locked
  // down to the review notice + Sign Out until the user signs out.
  underReview = false;

  // Cross-tab BroadcastChannel (plumbing — excluded from observability).
  _channel = null;

  constructor() {
    // The cross-tab channel is plumbing, not observable state.
    makeAutoObservable(this, {
      _channel: false,
      _broadcast: false,
      _applyRemote: false,
      _debugSnapshot: false,
    });
    this._initChannel();
  }

  // The full selected-org record (id/name/fein/roles/…) resolved from userOrgs
  // by the selected fein. Read-only convenience for views that need org display
  // fields; the canonical selection remains the fein in `currentOrg`.
  get currentOrgRecord() {
    if (!this.currentOrg) {
      return undefined;
    }
    return (this.userOrgs || []).find(
      (org) => org && org.fein === this.currentOrg,
    );
  }

  // Set the selected org by its business key (fein).
  setCurrentOrg(fein) {
    this.currentOrg = fein || undefined;
    debugLog('appStore', 'setCurrentOrg (fein)', this.currentOrg);
    this._broadcast('currentOrg', this.currentOrg);
  }

  // Select an org by its full record: register the record in userOrgs (dedup by
  // fein) so `currentOrgRecord` can resolve it even for admin-selected orgs that
  // are not among the user's own affiliations, then set the selection to its
  // fein. The canonical selection stored in `currentOrg` is always the fein.
  selectOrgRecord(record) {
    if (!record || !record.fein) {
      this.currentOrg = undefined;
      debugLog('appStore', 'selectOrgRecord (cleared \u2014 no fein)', record);
      this._broadcast('currentOrg', this.currentOrg);
      return;
    }
    this.userOrgs = [
      ...(this.userOrgs || []).filter((org) => org && org.fein !== record.fein),
      record,
    ];
    this.currentOrg = record.fein;
    debugLog('appStore', 'selectOrgRecord (fein)', record.fein, record);
    this._broadcast('currentOrg', this.currentOrg);
  }

  // Set the selected drug by its business key (ndc).
  setCurrentDrug(ndc) {
    this.currentDrug = ndc || undefined;
    debugLog('appStore', 'setCurrentDrug (ndc)', this.currentDrug);
    this._broadcast('currentDrug', this.currentDrug);
  }

  signOut() {
    // signOut() can run TWICE for a single logout — once directly from
    // signOutEverywhere and again from onAuthStateChanged(null) → clearUser. The
    // clear itself is idempotent, but the cross-tab broadcast should fire only
    // on the transition from signed-in to signed-out (not on the redundant
    // second call), so capture whether there was a session first.
    const wasSignedIn =
      this.loggedInUser !== null ||
      this.oidcUser !== null ||
      this.email !== undefined;
    this.loggedInUser = null;
    this.oidcUser = null;
    this.email = undefined;
    this.underReview = false;
    // Clear all session/selection UI state so the post-logout landing page
    // shows ONLY the sign-in screen — no stale "Session info" company modal
    // (CompanyPopup) and no leftover org/drug selection from the prior session.
    this.showMessage = false;
    this.currentOrg = undefined;
    this.currentDrug = undefined;
    this.userOrgs = [];
    this.userRoles = [];
    debugLog('appStore', 'signOut — session cleared', { wasSignedIn });
    if (wasSignedIn) {
      this._broadcast('signOut');
    }
  }

  // ---- cross-tab plumbing (never observable, never persisted) --------------

  _initChannel() {
    // BroadcastChannel is unavailable in some browsers and in the jsdom test
    // env; cross-tab sync is a best-effort enhancement, so degrade silently.
    if (typeof BroadcastChannel === 'undefined') {
      this._channel = null;
      return;
    }
    this._channel = new BroadcastChannel(SESSION_CHANNEL);
    this._channel.onmessage = (event) => this._applyRemote(event.data);
  }

  // Mirror a local session change to other tabs. A closed/unavailable channel
  // must never break local state.
  _broadcast(type, payload) {
    if (!this._channel) {
      return;
    }
    try {
      this._channel.postMessage({ type, payload });
    } catch {
      // ignore — local state has already been updated
    }
  }

  // Apply a session change received from another tab WITHOUT re-broadcasting
  // (so the two tabs do not echo each other into a loop).
  _applyRemote(message) {
    if (!message || !message.type) {
      return;
    }
    debugLog('appStore', 'cross-tab message received (new tab/other tab)', message);
    switch (message.type) {
      case 'currentOrg':
        this.currentOrg = message.payload || undefined;
        break;
      case 'currentDrug':
        this.currentDrug = message.payload || undefined;
        break;
      case 'signOut':
        // Mirror the FULL local sign-out clear (minus the re-broadcast) so a
        // sign-out in another tab leaves no stale selection/role/UI state here.
        this.loggedInUser = null;
        this.oidcUser = null;
        this.email = undefined;
        this.underReview = false;
        this.showMessage = false;
        this.currentOrg = undefined;
        this.currentDrug = undefined;
        this.userOrgs = [];
        this.userRoles = [];
        break;
      default:
        break;
    }
    this._debugSnapshot('cross-tab apply');
  }

  // Log a snapshot of the in-memory session/role state. Called at the key
  // retrieval moments (page load, tab focus, new tab, auth callbacks). No-op
  // unless the devLogger is enabled.
  _debugSnapshot(reason) {
    debugLog('appStore', `session snapshot (${reason})`, {
      email: this.email,
      currentOrg: this.currentOrg,
      currentDrug: this.currentDrug,
      userOrgs: this.userOrgs,
      loggedInUser: this.loggedInUser
        ? { email: this.loggedInUser.email, roles: this.loggedInUser.roles }
        : null,
      underReview: this.underReview,
    });
  }
})();

